'use client';

import React, {useEffect, useState} from 'react';
import {useRouter} from 'next/navigation';
import {
  _m,
  AWSManager,
  ClientPropertyConstants,
  CurrentUserStore,
  LoadingIndicator,
  Preferences,
  SKZPersonalInfo,
  SKZPlayerProfile,
  SMSVerifyController,
  WebUnityFeatureVersionManager,
} from '@sdk-mobile';

import getGameId from '@/utils/getGameId';
import safeReturnTo from '@/utils/safeReturnTo';
import {SKILLZ_APP_GAME_ID} from '@/app/(app)/partner/[slug]/_src/shellGame';
import Cookies from 'js-cookie';
import {FTUE_STATE, REGISTER_LOCK_COOKIE_NAME} from '@/utils/constant';
import {useSkillzStyle} from '@/app/(app)/games/[gid]/_src/SkillzStyleProvider';
import PhoneInput from '@/app/(app)/games/[gid]/sign-in/_src/PhoneInput';
import CodeInput from '@/app/(app)/games/[gid]/sign-in/_src/CodeInput';
import SignInLoader from '@/app/(app)/games/[gid]/sign-in/_src/SignInLoader';
import AdditionalInfo from '@/app/(app)/games/[gid]/sign-in/_src/AdditionalInfo';
import {getFtueCookieClient, setFtueCookie} from '@/utils/ftueCookieHelper';
import {useFirstFTUEGame} from '@/app/(app)/games/[gid]/_src/GameProviders/FTUEGameProvider';
import LoadingScreen from '@/app/(app)/games/[gid]/_src/LoadingScreen';

const AWS_EVENTS = {
  EXIT_PRESS: 'SMS_FTUE_SIGN_IN_EXIT',
  INFO_PRESS: 'SMS_FTUE_SIGN_IN_INFO',
  VIEW_PHONE_TAB: 'SMS_FTUE_SIGN_IN_PHONE_VIEW',
  VIEW_EMAIL_TAB: 'SMS_FTUE_SIGN_IN_EMAIL_VIEW',
  AREA_CODE_BOX_PRESS: 'SMS_FTUE_SIGN_IN_PHONE_AREACODE_TAP',
  SEND_CODE: 'SMS_FTUE_SIGN_IN_PHONE_SEND',
  SEND_CODE_CALL: 'SMS_FTUE_SIGN_IN_PHONE_CALL_SEND',
  ENTER_CODE: 'SMS_FTUE_SIGN_IN_SMS_ENTER',
  CODE_VERIFIED: 'SMS_FTUE_SIGN_IN_CODE_VERIFIED',
  USER_SIGNED_IN: 'SMS_FTUE_PREEXISTING_USER_PHONE_SIGN_IN',
};

const DEFAULT_COUNTRY = 'us';
const PHONE_IN_USE_ERROR = 'PHONE_IN_USE';

const noop = () => {};

type Props = {
  setShowBanner: (show: boolean) => void;
  liveEvent?: boolean;
};

type AdditionalInfo = {
  firstName?: string;
  lastName?: string;
  email?: string;
} | null;

const RegisterWithSMS = ({setShowBanner, liveEvent}: Props) => {
  const router = useRouter();
  const gameId = getGameId();
  const {gameTitle, gameIcon} = useSkillzStyle();
  const isFirstFTUEInProgress = useFirstFTUEGame();

  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [countryValue, setCountryValue] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [oldPhoneNumber, setOldPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState(false);
  const [errorStyle, setErrorStyle] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [showCountryList, setShowCountryList] = useState(false);
  const [resendEnabled, setResendEnabled] = useState(true);
  const [callInsteadEnabled, setCallInsteadEnabled] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<AdditionalInfo>(null);

  /* istanbul ignore next */ // not testing phone number logic
  const parsedNumber = phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '');

  const fullNumber = `${countryValue}${parsedNumber}`;

  useEffect(() => {
    if (country !== DEFAULT_COUNTRY) {
      return;
    }
    SMSVerifyController.fetchCountry(setCountry);
  }, [country]);

  useEffect(() => {
    const parsedInputNumber = phoneNumber.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
    const matchingCountry = parsedInputNumber?.startsWith('+')
      ? SMSVerifyController.getRegionFromPhoneNumber('+' + parsedNumber)
      : null;

    if (matchingCountry) {
      // Update state with the matched country details
      const currentCountryValue = SMSVerifyController.getCountryCode(matchingCountry);
      const currentPhone = phoneNumber.slice(currentCountryValue.length);
      setCountry(matchingCountry);
      setCountryValue(currentCountryValue);
      setPhoneNumber(SMSVerifyController.formatNumber(currentPhone, country));
    } else {
      const currentCountryValue = SMSVerifyController.getCountryCode(country);
      setCountryValue(currentCountryValue);
      setPhoneNumber(SMSVerifyController.formatNumber(phoneNumber, country));
    }

    setErrorStyle(false);
    setAlreadyRegistered(false);
    setResendEnabled(true); //reset timer if phone number is changed
    setCallInsteadEnabled(true); //reset timer if phone number is changed
  }, [country, phoneNumber]);

  const sendSMSVerification = (
    phone: string,
    onSuccess: () => void,
    onError: (error: string) => void,
    call?: boolean
  ) => {
    if (alreadyRegistered) {
      console.log(`RegisterWithSMS: Send code required to login on ${phone}`);
      SMSVerifyController.requestVerificationSignIn(phone, onSuccess, onError, call);
    } else {
      console.log(`RegisterWithSMS: Send code required to register on ${phone}`);
      SMSVerifyController.requestVerificationSignup(phone, onSuccess, onError, call);
    }
  };

  const editPress = () => {
    setErrorStyle(false);
    setVerificationCode(false);
  };

  const codeValidation = (
    code: string,
    phone: string,
    onSuccess: () => void,
    onFailure: (error: string) => void
  ): void => {
    const onSuccessWithRedirect = async () => {
      AWSManager.trackEvent(AWS_EVENTS.CODE_VERIFIED);
      Cookies.remove(REGISTER_LOCK_COOKIE_NAME);
      onSuccess();

      // Multicade SHELL (root/partner arcade, game 19369): sign-in is a "resume where you
      // were" step — after auth, land on `returnTo` (the arcade: /, /store, /partner/{slug}),
      // never /games/19369/home|tutorial or an FTUE match. The shell has no Unity build, so
      // every game-flow branch below dead-ends here; short-circuit to the arcade instead.
      // Real games (gid !== the shell id) fall through to their normal home/tutorial/FTUE.
      if (gameId === String(SKILLZ_APP_GAME_ID)) {
        AWSManager.trackEvent(AWS_EVENTS.USER_SIGNED_IN);
        Preferences.disableFTUE();
        setFtueCookie(FTUE_STATE.FINISHED);
        LoadingIndicator.hide();
        router.replace(safeReturnTo() ?? '/');
        return;
      }

      // If fetching profile fails we'll continue with tutorial just to keep user moving
      const profile: SKZPlayerProfile | undefined =
        await CurrentUserStore.fetchUserProfile(false).catch(noop);
      const skipTutorial = profile?.total_games_played > 0;
      const hasTutorial =
        ClientPropertyConstants.getFtueWebTutorialScreenEnabled() &&
        WebUnityFeatureVersionManager.isFeatureSupported('FTUE_SIGN_IN_TUTORIAL');
      const showFullFTUE = !alreadyRegistered;

      LoadingIndicator.hide();

      if (liveEvent) {
        // /play-now/ go from here
        // User is registering via live event link - ask for some additional info

        LoadingIndicator.show();
        const personalInfo: SKZPersonalInfo | undefined =
          await CurrentUserStore.getUserPersonalInfo().catch(noop);
        AWSManager.trackEvent('SMS_FTUE_SIGN_IN_CODE_VERIFIED');
        Preferences.disableFTUE();
        setFtueCookie(FTUE_STATE.FINISHED);
        LoadingIndicator.hide();
        setIsSigningIn(false);
        setAdditionalInfo({
          firstName: personalInfo?.first_name,
          lastName: personalInfo?.last_name,
          email: personalInfo?.email,
        });
      } else if (skipTutorial) {
        // /sign-in/ go from here
        // Seem like this user already played some games, so redirect to home

        AWSManager.trackEvent(AWS_EVENTS.USER_SIGNED_IN);
        Preferences.disableFTUE();

        //orignally there's logic to go back to root, but since this flow will eventally call MergeAccountController.onSuccessAccountMerge(), we can skip it here
      } else if (!hasTutorial) {
        // This is a new user and the game doesn't have tutorial, so redirect to first FTUE game// But only for new users

        AWSManager.trackEvent('SMS_FTUE_NO_GAME_TUTORIAL');
        if (!alreadyRegistered) {
          Preferences.enableFullFTUE();
          setFtueCookie(FTUE_STATE.ANONYMOUS);
        }

        console.log(
          'RegisterWithSMS: Code was correct, tutorial not available, first FTUE game will start'
        );
      } else {
        // Enable FTUE only if it's not passed. User might finish FTUE on different game

        if (getFtueCookieClient() !== FTUE_STATE.FINISHED && showFullFTUE) {
          Preferences.enableFullFTUE();
          setFtueCookie(FTUE_STATE.ANONYMOUS);
        }
        console.log('RegisterWithSMS: Code was correct, redirect to tutorial');
        router.replace(`/games/${gameId}/tutorial`);
      }
    };

    if (alreadyRegistered) {
      console.log(`RegisterWithSMS: Try to log in ${phone} with code ${code}`);
      SMSVerifyController.loginWithSMS(
        code,
        phone,
        onSuccessWithRedirect,
        onFailure,
        true,
        !liveEvent
      );
    } else {
      console.log(`RegisterWithSMS: Try to register ${phone} with code ${code}`);
      SMSVerifyController.confirmVerification(code, phone, onSuccessWithRedirect, onFailure);
    }
  };

  const handleSMSValidationError = (errorString: string, phoneNumber: string) => {
    if (errorString === PHONE_IN_USE_ERROR) {
      console.log(
        `RegisterWithSMS: Number ${phoneNumber} already registered, try to sign in instead`
      );
      setAlreadyRegistered(true);
      const noSpecialCharacters = phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '');
      SMSVerifyController.requestVerificationSignIn(
        `${countryValue}${noSpecialCharacters}`,
        () => setVerificationCode(true),
        (errorString) => {
          setErrorMessage(errorString);
          setErrorStyle(true);
        }
      );
    } else {
      setErrorMessage(errorString);
      setErrorStyle(true);
    }
  };

  const onSendPressed = () => {
    if (verificationCode) {
      // Resend code
      sendSMSVerification(
        fullNumber,
        () => {},
        (errorString) => {
          handleSMSValidationError(errorString, parsedNumber);
        }
      );
      AWSManager.trackEvent(AWS_EVENTS.SEND_CODE);
      setShowBanner(true);
      setResendEnabled(false);
    } else {
      // Send code
      if (!SMSVerifyController.isValidNumber(fullNumber, country)) {
        /* istanbul ignore next */ // not testing the code input state
        setErrorMessage(_m('Invalid phone number'));
        setErrorStyle(true);
      } else {
        /* istanbul ignore next */ // not testing the code input state
        sendSMSVerification(
          fullNumber,
          () => setVerificationCode(true),
          (errorString) => {
            handleSMSValidationError(errorString, parsedNumber);
          }
        );
        AWSManager.trackEvent(AWS_EVENTS.SEND_CODE);
      }
    }
  };

  const callMeInsteadPress = () => {
    sendSMSVerification(
      fullNumber,
      () => {},
      (errorString) => {
        handleSMSValidationError(errorString, parsedNumber);
      },
      true
    );
    setCallInsteadEnabled(false);
  };

  useEffect(() => {
    if (phoneNumber.length - oldPhoneNumber.length > 0) {
      if (SMSVerifyController.isValidNumber(fullNumber, country)) {
        onSendPressed();
      }
    }
    setOldPhoneNumber(phoneNumber);
  }, [country, oldPhoneNumber.length, onSendPressed, phoneNumber, fullNumber]);

  if (isFirstFTUEInProgress) {
    return <LoadingScreen />;
  }

  if (isSigningIn) {
    return <SignInLoader gameIcon={gameIcon} gameTitle={gameTitle} />;
  }

  if (additionalInfo !== null) {
    return (
      <AdditionalInfo
        firstName={additionalInfo?.firstName}
        lastName={additionalInfo?.lastName}
        email={additionalInfo?.email}
      />
    );
  }

  if (verificationCode) {
    return (
      <CodeInput
        callInsteadEnabled={callInsteadEnabled}
        callMeInsteadPress={callMeInsteadPress}
        countryValue={countryValue}
        editPress={editPress}
        errorStyle={errorStyle}
        errorMessage={errorMessage}
        gameIcon={gameIcon}
        gameTitle={gameTitle}
        phoneNumber={phoneNumber}
        resendEnabled={resendEnabled}
        setCallInsteadEnabled={setCallInsteadEnabled}
        setErrorMessage={setErrorMessage}
        setErrorStyle={setErrorStyle}
        setIsSigningIn={setIsSigningIn}
        setResendEnabled={setResendEnabled}
        setShowBanner={setShowBanner}
        codeValidation={codeValidation}
        onSendPressed={onSendPressed}
        liveEvent={liveEvent}
      />
    );
  }

  return (
    <PhoneInput
      country={country}
      countryValue={countryValue}
      errorStyle={errorStyle}
      errorMessage={errorMessage}
      gameIcon={gameIcon}
      gameTitle={gameTitle}
      phoneNumber={phoneNumber}
      setCountry={setCountry}
      setPhoneNumber={setPhoneNumber}
      setShowCountryList={setShowCountryList}
      showCountryList={showCountryList}
      onSendPressed={onSendPressed}
      liveEvent={liveEvent}
    />
  );
};

export default RegisterWithSMS;
